import tooltips from '../../../../../../../store/tooltips';

/**
 * Specifies the width of all the forms under General Info
 */
export const formMaxWidth = 1200;

/**
 * Associated units inside of the FeedstockSource.js form
 */
export const associatedMeasurementUnits = {
    'Imperial': [
        'Tonnes', 'Acres'
    ],
    'Metric': [
        'Tonnes', 'Hectares'
    ]
}

/**
 * Used to populate the select form field inside of FeedstockSource.js
 */
export const feedstockSourceTypes = [
    'Hardwood',
    'Softwood',
    'Mixed',
    'Agricultural Waste'
]

/**
 * Used to populate the select form field inside of BusinessAsUsualForFeedstock.js
 */
export const feedstockUses = [
    'It would be burned (pile burn)',
    'It would be sold as firewood',
    'It would be chipped',
    'It would be left to decay',
];

export function formatFilename(filename) {
    const splitPath = filename.split('/')
    const arrayLength = splitPath.length
    return splitPath[arrayLength - 1]
}

/**
 * Used to populate the buttons in GeneralInfo.js
 */
export const generalInfoCategories = [
    'Project Description',
    'Business as Usual for Feedstock',
    'Biochar and Water Supply Equipment',
    'Feedstock Source',
    'Burn Supervisor',
    'Project Stakeholders',
    'Entity Details',
]

/**
 * Defines the default form state. Used in conjunction with the selected category string to 'pull out' the correct default form state dynamically.
 * - Note: 'File' keys must have a value set to null.
 * - Note: Numbers are also set to a null value.
 */
export const defaultGeneralInfoFormState = {
    'Project Description': {
        description: '',
        location: '',
        timeFrame: '',
        entityRoles: '',
    },
    'Business as Usual for Feedstock': {
        feedstockType: '',
        feedstockUse: '',
        hasEvidence: '',
        evidenceUpload: null,
    },
    'Biochar and Water Supply Equipment': {
        biocharEquipment: '',
        waterSupplyEquipment: '',
    },
    'Feedstock Source': {
        feedstockType: '',
        feedstockDetails: '',
        location: '',
        feedstockAmount: null,
        feedstockUnit: '',
    },
    'Burn Supervisor': {
        isTrained: '',
        supervisorName: '',
        supervisorInfo: '',
    },
    'Project Stakeholders': {
        stakeholderDetails: '',
        stakeholderName: '',
        stakeholderRole: '',
        stakeholderCompany: '',
        stakeholderContact: '',
        proofOfInvolvement: null,
    },
    "Entity Details": {
        organisationName: '',
        organisationAddress: '',
        organisationEmail: '',
        organisationLogo: null,
        clientName: '',
        clientAddress: '',
        clientContact: '',
        clientLogo: null,
    }
}

/**
 * Contains the text, and default values that will populate a given form component. 
 * @param {{description: string, location: string, timeFrame: string, entityRoles: string}} formState The current formState handled by general-info-hooks.js hook.
 * @returns {Array<{type:'text' | 'select' | 'radio' | 'file',fieldId: string, text: string, value: string, options?: Array<string>, selectPlaceholder?: string, multiline?: boolean, rows?: number, placeholder?: string, tooltip?: string}>} An array of form objects with attributes designed to be looped over and passed as props. Works as the left hand to the dynamic form's right hand.
 */
export const projectDescriptionQuestions = (formState) => {
    const { description, location, timeFrame, entityRoles } = formState
    return [
        {
            type: 'text',
            fieldId: 'description',
            text: 'Please provide a short paragraph description of this project:',
            value: description,
            multiline: true,
            rows: 3,
            placeholder: 'Describe the project.'
        },
        {
            type: 'text',
            fieldId: 'location',
            text: 'Where is the project taking place?',
            value: location,
            placeholder: 'Enter location.'
        },
        {
            type: 'text',
            fieldId: 'timeFrame',
            text:
                'When is the project starting and what is the predicted time frame for the activities?',
            value: timeFrame,
            placeholder: 'Project start time and details.'
        },
        {
            type: 'text',
            fieldId: 'entityRoles',
            text:
                'Please list all the main entities involved in this project and their role:',
            value: entityRoles,
            placeholder: 'Details of collaboration.'
        },
    ]
}

/**
 * Contains the text, and default values that will populate a given form component. 
 * @param {{feedstockType: string, feedstockUse: string, hasEvidence: string, evidenceUpload: {fileurl: string, filename: string} | null }} formState The current formState handled by general-info-hooks.js hook.
 * @returns {Array<{type:'text' | 'select' | 'radio' | 'file', fieldId: string, text: string, value: string, options?: Array<string>, selectPlaceholder?: string, multiline?: boolean, rows?: number, placeholder?: string, tooltip?: string}>} An array of form objects with attributes designed to be looped over and passed as props. Works as the left hand to the dynamic form's right hand.
 */
export const businessAsUsualForFeedstockQuestions = (formState) => {
    const { hasEvidence, feedstockType, feedstockUse } = formState
    const defaultQuestions = [
        {
            type: 'select',
            fieldId: 'feedstockType',
            text: 'Select the primary feedstock type:',
            value: feedstockType,
            options:
                feedstockSourceTypes,
            selectPlaceholder: 'Primary Feedstock',
            tooltip: tooltips.businessAsUsualQ1AndQ2
        },
        {
            type: 'select',
            fieldId: 'feedstockUse',
            text: 'What would normally be done with this feedstock?',
            value: feedstockUse,
            options:
                feedstockUses,
            selectPlaceholder: 'Usual Feedstock Business'
        },
        {
            type: 'radio',
            fieldId: 'hasEvidence',
            text: 'Can you provide evidence of the alternative fate for this feedstock?',
            value: hasEvidence,
        },
    ]

    const optionalQuestions = [
        {
            type: 'file',
            fieldId: 'evidenceUpload',
            text: 'Please upload evidence for the alternative fate of feedstock:',
        },
    ]

    return [
        ...defaultQuestions,
        ...(hasEvidence === 'yes' ? optionalQuestions : [])
    ]
}

/**
 * Contains the text, and default values that will populate a given form component. 
 * @param {{biocharEquipment: string, waterSupplyEquipment: string}} formState The current formState handled by general-info-hooks.js hook.
 * @returns {Array<{type:'text' | 'select' | 'radio' | 'file',fieldId: string, text: string, value: string, options?: Array<string>, selectPlaceholder?: string, multiline?: boolean, rows?: number, placeholder?: string, tooltip?: string}>} An array of form objects with attributes designed to be looped over and passed as props. Works as the left hand to the dynamic form's right hand.
 */
export const biocharAndWaterSupplyEquipmentQuestions = (formState) => {
    const { biocharEquipment, waterSupplyEquipment } = formState
    return [
        {
            type: 'text',
            fieldId: 'biocharEquipment',
            text: 'Describe your biochar production equipment:',
            value: biocharEquipment,
            multiline: true,
            rows: 3,
            placeholder: 'Kilns, rakes, shovels, measurement tools, sundry tools.'
        },
        {
            type: 'text',
            fieldId: 'waterSupplyEquipment',
            text: 'Describe the fire-fighting equipment and water supply equipment that will be utilized during this project:',
            value: waterSupplyEquipment,
            multiline: true,
            rows: 3,
            placeholder: 'Describe water supply equipment (capacity, number of) - eg. Water tenders, backpack spray pumps, backup pumps, hose length, misting system, etc.'
        },
    ];
}

/**
 * Contains the text, and default values that will populate a given form component. 
 * @param {{feedstockType: string, feedstockDetails: string, location: string, feedstockAmount: number | null, feedstockUnit: string}} formState The current formState handled by general-info-hooks.js hook.
 * @returns {Array<{type:'text' | 'select' | 'radio' | 'file',fieldId: string, text: string, value: string, options?: Array<string>, selectPlaceholder?: string, multiline?: boolean, rows?: number, placeholder?: string, tooltip?: string}>} An array of form objects with attributes designed to be looped over and passed as props. Works as the left hand to the dynamic form's right hand.
 */
export const feedstockSourceQuestions = (formState, derivedUnitsArray) => {
    const { feedstockType, feedstockDetails, location, feedstockAmount, feedstockUnit } = formState
    const validFeedstockUnit = derivedUnitsArray?.includes(feedstockUnit) ? feedstockUnit : '';

    return [
        {
            type: 'select',
            fieldId: 'feedstockType',
            text: 'Select primary feedstock type:',
            value: feedstockType,
            options:
                feedstockSourceTypes,
            selectPlaceholder: 'Primary Feedstock Type',
            tooltip: tooltips.planningQ1,
        },
        {
            type: 'text',
            fieldId: 'feedstockDetails',
            text: 'Please detail the Woody Biomass Species that will be used:',
            value: feedstockDetails,
            placeholder: 'Please describe your feedstock in terms of volumes and composition of mix of species - if applicable',
            tooltip: tooltips.planningQ2,
        },
        {
            type: 'text',
            fieldId: 'location',
            text: 'Location:',
            value: location,
            placeholder: 'Enter what3words.com location here',
            alert: true,
            tooltip: tooltips.planningQ3,
        },
        {
            type: 'text',
            fieldId: 'feedstockAmount',
            text: 'Please specify the quantity of feedstock available:',
            value: feedstockAmount,
            placeholder: 'Enter quantity',
            tooltip: tooltips.planningQ4,
        },
        {
            type: 'select',
            fieldId: 'feedstockUnit',
            text: 'Select the appropriate unit that the quantity of feedstock was measured in:',
            value: validFeedstockUnit,
            options:
                derivedUnitsArray,
            selectPlaceholder: 'Unit',
        },
    ]
}

/**
 * Contains the text, and default values that will populate a given form component. 
 * @param {{isTrained: string, supervisorName: string, supervisorInfo: string}} formState The current formState handled by general-info-hooks.js hook.
 * @returns {Array<{type:'text' | 'select' | 'radio' | 'file',fieldId: string, text: string, value: string, options?: Array<string>, selectPlaceholder?: string, multiline?: boolean, rows?: number, placeholder?: string, tooltip?: string}>} An array of form objects with attributes designed to be looped over and passed as props. Works as the left hand to the dynamic form's right hand.
 */
export const burnSupervisorQuestions = (formState) => {
    const { isTrained, supervisorName, supervisorInfo } = formState
    const defaultQuestions = [
        {
            type: 'radio',
            fieldId: 'isTrained',
            text: 'Is a trained contractor team managing the process on site?',
            value: isTrained,
            tooltip: tooltips.burnSupervisorQ1,
        },
    ]
    const optionalQuestions = [
        {
            type: 'text',
            fieldId: 'supervisorName',
            text: 'Please detail the name of the burn supervisor, and their company/organization:',
            value: supervisorName,
            placeholder: 'Supervisor name and association',
            tooltip: tooltips.burnSupervisorQ2,
        },
        {
            type: 'text',
            fieldId: 'supervisorInfo',
            text: 'Please provide details about their role and relevant experience:',
            value: supervisorInfo,
            placeholder: 'Burn supervisor role and details',
        },
    ]

    return [
        ...defaultQuestions,
        ...(isTrained === 'yes' ? optionalQuestions : isTrained === 'no' ? [{ type: 'No Supervisor' }] : [])
    ]
}


/**
 * Contains the text, and default values that will populate a given form component. 
 * @param {{stakeholderDetails: string, stakeholderName: string, stakeholderRole: string, stakeholderCompany: string, stakeholderContact: string, proofOfInvolvement: null | {fileurl: string, filename: string} }} formState The current formState handled by general-info-hooks.js hook.
 * @returns {Array<{type:'text' | 'select' | 'radio' | 'file', fieldId: string, text: string, value: string, options?: Array<string>, selectPlaceholder?: string, multiline?: boolean, rows?: number, placeholder?: string, tooltip?: string}>} An array of form objects with attributes designed to be looped over and passed as props. Works as the left hand to the dynamic form's right hand.
 */
export const projectStakeholderQuestions = (formState) => {
    const { stakeholderDetails, stakeholderName, stakeholderRole,
        stakeholderCompany, stakeholderContact } = formState

    return [
        {
            type: 'text',
            fieldId: 'stakeholderDetails',
            text: 'Please list any other stakeholders involved in the project during planning, design, implementation or post-implementation:',
            value: stakeholderDetails,
            placeholder: 'May include Funders, Project Developers, Office Administrators, Forestry Contractors, Laboratories, Soil Specialists, etc.',
            rows: 3,
            multiline: true,
            tooltip: tooltips.proofProjectQ1
        },
        {
            type: 'text',
            fieldId: 'stakeholderName',
            text: 'Stakeholder name:',
            value: stakeholderName,
            placeholder: 'Stakeholder name details',
        },
        {
            type: 'text',
            fieldId: 'stakeholderRole',
            text: 'Stakeholder role:',
            value: stakeholderRole,
            placeholder: 'Stakeholder role details'
        },
        {
            type: 'text',
            fieldId: 'stakeholderCompany',
            text: 'Stakeholder company:',
            value: stakeholderCompany,
            placeholder: 'Stakeholder company details'
        },
        {
            type: 'text',
            fieldId: 'stakeholderContact',
            text: 'Stakeholder contact e-mail:',
            value: stakeholderContact,
            placeholder: 'Stakeholder contact e-mail'
        },
        {
            type: 'file',
            fieldId: 'proofOfInvolvement',
            text: 'Please provide proof of their involvement (Contract or other documentation)',
        }
    ]
}

/**
 * Used to populate the rows in the table of the Project Stakeholders form
 */
export const projectStakeholdersTableColumns = [
    'Stakeholder Name',
    'Stakeholder Role',
    'Stakeholder Company',
    'Stakeholder Contact',
    'Stakeholder Details',
    'Proof of Involvement',
]

export const entityDetailsQuestions = (formState) => {
    const { organisationName, organisationAddress, organisationEmail, organisationLogo,
        clientName, clientAddress, clientContact, clientLogo } = formState
    return [
        {
            type: 'text',
            text: 'Name of your organisation:',
            fieldId: 'organisationName',
            value: organisationName,
            placeholder: `Organisation's name`
        },
        {
            type: 'text',
            text: `Organisation's address:`,
            fieldId: 'organisationAddress',
            value: organisationAddress,
            placeholder: `Organisation's address`
        },
        {
            type: 'text',
            text: `Organisation's main contact e-mail:`,
            fieldId: 'organisationEmail',
            value: organisationEmail,
            placeholder: `Organisation's e-mail`
        },
        {
            type: 'file',
            text: `Organisation's logo:`,
            fieldId: 'organisationLogo',
            value: organisationLogo,
        },
        {
            type: 'text',
            text: `Client's name (individual or organization):`,
            fieldId: 'clientName',
            value: clientName,
            placeholder: 'Client name'
        },
        {
            type: 'text',
            text: `Client's address:`,
            fieldId: 'clientAddress',
            value: clientAddress,
            placeholder: `Client's address`
        },
        {
            type: 'text',
            text: `Client's primary e-mail:`,
            fieldId: 'clientContact',
            value: clientContact,
            placeholder: `Client's e-mail`
        },
        {
            type: 'file',
            text: `Client's logo:`,
            fieldId: 'clientLogo',
            value: clientLogo,
        },
    ]
}